
require("./../../../node_modules/@module-federation/nextjs-mf/src/include-defaults");
import MainLayout from '@/layouts/MainLayout'
import {
  McButton,
  McInput,
  McNotification,
} from '@maersk-global/mds-react-wrapper'
import s from './styles.module.scss'
import React, {ChangeEvent, ReactNode, useEffect, useState} from 'react'
import Link from 'next/link'
import {FieldErrors, SubmitHandler, useForm} from 'react-hook-form'
import {EMAIL_REGEX} from '@/utils/constants'
import {useUserStore} from '@/providers/userProvider'
import {useRouter} from 'next/router'
import {useSearchParams} from 'next/navigation'
import {isTokenValid} from '@/utils/token'

interface HomePageProps {}

interface FormValues {
  email: string
  password: string
}

const HomePage = () => {
  const {
    register,
    handleSubmit,
    clearErrors,
    getValues,
    setValue,
    formState: {errors},
  } = useForm<FormValues>({
    mode: 'onBlur',
  })
  const {login, error, isLoading, user} = useUserStore()
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)
  const router = useRouter()
  const searchParams = useSearchParams()
  const hasExpiredParam = searchParams.get('expired')

  useEffect(() => {
    const externalRoutes = ['resetpassword', 'criarpassword']
    const currentRoute = router.asPath

    if (externalRoutes.some(route => currentRoute.includes(route))) {
      router.push(currentRoute)
    }

    if (user?.exp && isTokenValid(user.exp)) {
      router.push('/ordens')
    }
  }, [router, user?.exp, searchParams])

  const shouldDisplayErrorToast = () => {
    if (error) return true
    if (hasExpiredParam && !isLoading) return true
    if (user) {
      return !isTokenValid(user.exp)
    }
    return false
  }

  const onSubmit: SubmitHandler<FormValues> = data => {
    try {
      clearErrors()
      login(data.email, data.password)
    } catch (e) {
      console.log('error', e)
    }
  }

  const isValidEmail = (value: string) => EMAIL_REGEX.test(value)

  const getResponseError = () => {
    if (error) {
      return error.response?.data.Message
    }
    if (hasExpiredParam ?? (user && isTokenValid(user.exp))) {
      return 'Sessão expirada. Por favor, faça login novamente.'
    }
    return 'Ocorreu um erro ao realizar seu login. Por favor, tente novamente mais tarde.'
  }

  const getEmailErrorMessage = (err: FieldErrors<FormValues>) => {
    if (err.email?.type === 'required') return 'O email é obrigatório'
    if (err.email?.type === 'validate') return 'Digite um email válido'
    return undefined
  }

  return (
    <div className={s.container}>
      <div className="mds">
        <h1 className="mds-headline--large">Login</h1>
      </div>
      {shouldDisplayErrorToast() && (
        <div className="mt-5">
          <McNotification
            icon="info-circle"
            body={getResponseError()}
            appearance="error"
          />
        </div>
      )}
      <form className="mt-5" onSubmit={handleSubmit(onSubmit)}>
        <div className={s.inputsContainer}>
          <McInput
            data-cy-item="login-input-email"
            variant="vanity"
            label={undefined}
            placeholder="Email"
            maxlength={150}
            fit="large"
            icon="user-circle"
            invalid={!!errors.email}
            errormessage={getEmailErrorMessage(errors)}
            required
            onInput={e => {
              const event = e as unknown as ChangeEvent<HTMLInputElement>
              const email = getValues('email')
              if (!email) {
                setValue('email', event.target.value)
              }
            }}
            {...register('email', {required: true, validate: isValidEmail})}
          />
          <div className="mt-4">
            <McInput
              data-cy-item="login-input-senha"
              label={undefined}
              icon="lock"
              variant="vanity"
              placeholder="Senha"
              maxlength={150}
              fit="large"
              type={isPasswordVisible ? 'text' : 'password'}
              clearbutton
              errormessage="A senha é obrigatória"
              required
              clickabletrailingicon
              trailingicon={isPasswordVisible ? 'eye-slash' : 'eye'}
              trailingiconclick={() =>
                setIsPasswordVisible(prevState => !prevState)
              }
              invalid={!!errors.password}
              onInput={e => {
                const event = e as unknown as ChangeEvent<HTMLInputElement>
                const passwordValue = getValues('password')
                if (!passwordValue) {
                  setValue('password', event.target.value)
                }
              }}
              {...register('password', {required: true})}
            />
          </div>
        </div>
        <div className="mt-10">
          <McButton
            data-cy-item="login-btn-login"
            label="Login"
            type="submit"
            loading={isLoading || !!user}
          />
          <McButton
            disabled={isLoading}
            type="button"
            label="Cadastre-se"
            variant="outlined"
            className="ml-4"
            onClick={() => router.push('/registrar')}
          />
        </div>
        <span className="block mt-4">
          Precisa de ajuda com a sua{' '}
          <Link href="/solicitarresetsenha">senha</Link>?
        </span>
      </form>
    </div>
  )
}

const getLayout = (page: ReactNode) => {
  return <MainLayout>{page}</MainLayout>
}

HomePage.getLayout = getLayout

export default HomePage
